import { useState, useCallback } from 'react'

import { useUploadImage } from './useUploadImage'

export interface UploadedImage {
  file: File
  preview: string
  progress: number
  name: string
  presignedUrl?: string
  fileKey?: string
  tempKey?: string
  sessionId?: string
}

interface UseImageChangeReturn {
  uploadedImages: UploadedImage[]
  error: string
  handleImageChange: (e: React.FormEvent<EventTarget>) => Promise<void>
  removeImage: (index: number) => void
  clearImages: () => void
  setError: (error: string) => void
}

export function useImageChange(): UseImageChangeReturn {
  const { uploadImages: uploadImagesToServer } = useUploadImage()
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])
  const [error, setError] = useState('')

  const handleImageChange = useCallback(
    async (e: React.FormEvent<EventTarget>) => {
      const files = (e.target as HTMLInputElement).files
      if (files) {
        const validFiles: File[] = []

        Array.from(files).forEach(file => {
          const isValidFormat =
            file.name.match(/\.(png|jpg|jpeg)$/i) ||
            file.type === 'image/png' ||
            file.type === 'image/jpeg' ||
            file.type === 'image/jpg'

          if (!isValidFormat) {
            setError('Image format must be .png, .jpg or .jpeg.')
            return
          }

          const MAX_SIZE = 5 * 1024 * 1024
          if (file.size > MAX_SIZE) {
            setError('Image size cannot exceed 5MB.')
            return
          }
          validFiles.push(file)
        })

        if (validFiles.length === 0) return

        try {
          setError('')
          const uploadedImageData = await uploadImagesToServer(validFiles)

          const newImages: UploadedImage[] = uploadedImageData.map(imgData => ({
            file: imgData.file,
            preview: imgData.preview,
            progress: 100,
            name: imgData.name,
            presignedUrl: imgData.presignedUrl,
            fileKey: imgData.fileKey,
            tempKey: imgData.tempKey,
            sessionId: imgData.sessionId,
          }))

          setUploadedImages(prev => [...prev, ...newImages])
        } catch (err) {
          setError('Failed to upload images. Please try again.')
          console.error('Image upload error:', err)
        }
      }
      ;(e.target as HTMLInputElement).value = ''
    },
    [uploadImagesToServer]
  )

  const removeImage = useCallback((index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index))
  }, [])

  const clearImages = useCallback(() => {
    setUploadedImages([])
    setError('')
  }, [])

  return {
    uploadedImages,
    error,
    handleImageChange,
    removeImage,
    clearImages,
    setError,
  }
}
